import type { Offer } from "@/api/offerBank";

import { OFFER_CHIP_CLASSES } from "@/grapesPlugins/email/utils/deriveOfferCodeStyling";

export const OFFER_UNCONFIGURED_LABEL = "Choose an offer";

/**
 * Attribute holding the chip's brand-kit style variant as the full class name
 * (FOG-1962). Stored as a data attribute — never as a model class — because
 * the RTE selection plugin pollutes the DOM classList.
 */
export const OFFER_STYLE_ATTR = "data-offer-style";
export const OFFER_STYLE_DEFAULT = OFFER_CHIP_CLASSES.hero;

export const OFFER_CODE_SVG_ICON = `<svg viewBox="0 0 26 26" fill="currentColor" xmlns="http://www.w3.org/2000/svg"><path d="M13.3525 11.2744H14.7168V15.0635H13.3525V11.2744ZM13.3525 5.36523H3.11035C2.64718 5.36523 2.20324 5.54911 1.87598 5.87598C1.54883 6.2028 1.36523 6.64562 1.36523 7.10742V8.9043C2.25862 9.04699 3.09113 9.4671 3.73926 10.1143C4.55015 10.9242 5.00676 12.0232 5.00684 13.1689C5.00684 14.3148 4.55022 15.4136 3.73926 16.2236C3.09111 16.8709 2.25872 17.2909 1.36523 17.4336V19.2314C1.36532 19.6932 1.54882 20.1361 1.87598 20.4629C2.20323 20.7897 2.6472 20.9736 3.11035 20.9736H13.3525V18.5488H14.7168V20.9736H22.5322C22.9953 20.9735 23.4394 20.7897 23.7666 20.4629C24.0937 20.1361 24.2773 19.6931 24.2773 19.2314V17.4336C23.3836 17.2909 22.5506 16.8711 21.9023 16.2236C21.0915 15.4137 20.6357 14.3147 20.6357 13.1689C20.6358 12.0232 21.0914 10.9242 21.9023 10.1143C22.5506 9.46685 23.3837 9.04694 24.2773 8.9043V7.10742C24.2773 6.64573 24.0936 6.20278 23.7666 5.87598C23.4394 5.5492 22.9953 5.36534 22.5322 5.36523H14.7168V7.79004H13.3525V5.36523ZM25.6416 10.2148H24.96C24.175 10.2148 23.4221 10.526 22.8672 11.0801C22.3124 11.6342 22.0001 12.3856 22 13.1689C22 13.9523 22.3123 14.7046 22.8672 15.2588C23.422 15.8127 24.1751 16.124 24.96 16.124H25.6416V19.2314C25.6415 20.0556 25.3137 20.8462 24.7305 21.4287C24.1473 22.0111 23.3565 22.3378 22.5322 22.3379H3.11035C2.2858 22.3379 1.49443 22.0113 0.911133 21.4287C0.327912 20.8462 8.59545e-05 20.0556 0 19.2314V16.124H0.682617C1.46761 16.124 2.22051 15.8129 2.77539 15.2588C3.33027 14.7046 3.6416 13.9524 3.6416 13.1689C3.64152 12.3856 3.3302 11.6342 2.77539 11.0801C2.22049 10.5259 1.4677 10.2148 0.682617 10.2148H0V7.10742C0 6.28323 0.327954 5.49275 0.911133 4.91016C1.49443 4.32756 2.2858 4 3.11035 4H22.5322C23.3566 4.00011 24.1473 4.32768 24.7305 4.91016C25.3138 5.49278 25.6416 6.28312 25.6416 7.10742V10.2148Z" /></svg>`;

export const OFFER_PLACEHOLDER_RE = /\$OFFER/g;

export function buildOfferTag(slug: string): string {
  return `{% offer '${slug}' %}`;
}

export function resolveOfferPlaceholder(text: string, slug: string): string {
  return text.replace(OFFER_PLACEHOLDER_RE, buildOfferTag(slug));
}

export function getOfferPreview(offer: Offer | undefined | null): string {
  if (!offer) return "##########";
  return (
    offer.connector?.pattern ||
    (offer.code_source_type === "static" ? offer.static_code : "##########")
  );
}

/** Default fallback code for frame variant config and previews (matches email/HTML editors). */
export function getOfferFallbackCode(offer: Offer | undefined | null): string {
  return offer?.static_code ?? "";
}

/** Discount code shown in offer pickers (static code, pattern, or slug). */
export function getOfferDiscountCodeLabel(offer: Offer): string {
  if (offer.code_source_type === "static") {
    return offer.static_code;
  }
  return offer.connector?.pattern || offer.slug;
}
